import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { ArrowLeft, Loader2 } from "lucide-react";
import { canRetryWorkflowRun } from "@proclaw/shared";
import { cn } from "@/lib/utils";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { workflowRunsApi } from "@/api/workflows";
import { chatApi } from "@/api/chat";
import { useTouchAgent } from "@/hooks/use-agent-prefs";
import {
  workflowInsightsApi,
  type InsightRunRow,
  type InsightStep,
} from "@/api/workflow-insights";
import { queryKeys } from "@/lib/query-keys";
import { withWorkspaceContext } from "@/lib/workspace-navigation";
import { KpiStrip } from "@/components/ui/kpi-strip";
import { Segmented } from "@/components/ui/segmented";
import { AgentAvatar } from "@/components/agent/agent-avatar";
import { WorkflowTabs } from "@/components/workflows/workflow-tabs";
import {
  InsightsRangePicker,
  rangeKey,
  rangeQuery,
  useInsightsRange,
} from "@/components/workflows/insights/range-picker";
import {
  formatCostCents,
  formatDurationMs,
  formatInt,
  formatPercent,
  formatSignedPercent,
  formatStartedAt,
  formatTokens,
} from "@/lib/format";

/**
 * Workflow drill-in (design 5b): one workflow's node chain, scoped KPIs, its
 * runs, and a selected run opened to the step level with per-node duration,
 * tokens, cost and errors. `?run=` selects a run; the range params scope
 * the numbers (`?period=`, or `?since=&until=` with `?compare=`).
 */
export function WorkflowInsightsDetail() {
  const { workflowId } = useParams<{ workflowId: string }>();
  // Retry needs agent admin or above on the server; standard users only look.
  const { tenantId, canManageAnyAgent: canActOnRuns } = useTenant();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const touchAgent = useTouchAgent();

  const [range, setRange] = useInsightsRange();
  const rangeK = rangeKey(range);
  const selectedRunId = searchParams.get("run");
  const setParam = (key: string, value: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (value === null) params.delete(key);
    else params.set(key, value);
    setSearchParams(params, { replace: true });
  };

  const [runFilter, setRunFilter] = useState<"all" | "failed">("all");
  const [pageCount, setPageCount] = useState(1);
  useEffect(() => setPageCount(1), [runFilter, rangeK]);

  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const agentById = useMemo(() => new Map(agents.map((a) => [a.id, a])), [agents]);

  const insights = useQuery({
    queryKey: ["workflow-insights", "workflow", workflowId, rangeK],
    queryFn: () => workflowInsightsApi.workflow(workflowId!, rangeQuery(range)),
    enabled: !!workflowId,
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });

  const PAGE = 50;
  const runsQuery = useQuery({
    queryKey: ["workflow-insights", "runs", workflowId, rangeK, runFilter, pageCount],
    queryFn: () =>
      workflowInsightsApi.runs(workflowId!, {
        ...rangeQuery(range),
        status: runFilter === "failed" ? "failed" : null,
        limit: PAGE * pageCount,
      }),
    enabled: !!workflowId,
    placeholderData: keepPreviousData,
    refetchInterval: 10_000,
  });
  const runs = runsQuery.data?.runs ?? [];

  // Default the panel to the newest run so the page never opens empty.
  useEffect(() => {
    if (!selectedRunId && runs.length > 0) setParam("run", runs[0]!.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRunId, runs.length]);

  const runDetail = useQuery({
    queryKey: ["workflow-insights", "run", selectedRunId],
    queryFn: () => workflowInsightsApi.run(selectedRunId!),
    enabled: !!selectedRunId,
    refetchInterval: (q) => {
      const s = q.state.data?.run.status;
      return s === "running" || s === "awaiting" ? 3_000 : false;
    },
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["workflow-insights"] });
    if (workflowId) queryClient.invalidateQueries({ queryKey: queryKeys.workflowRuns.byWorkflow(workflowId) });
  };

  const [actionError, setActionError] = useState<string | null>(null);
  const runNow = useMutation({
    mutationFn: () => workflowRunsApi.start(workflowId!, {}),
    onMutate: () => setActionError(null),
    onSuccess: (run) => {
      invalidateAll();
      setParam("run", run.id);
    },
    onError: (e: unknown) => setActionError(e instanceof Error ? e.message : "Could not start a run"),
  });
  const retry = useMutation({
    mutationFn: (runId: string) => workflowRunsApi.retry(runId),
    onMutate: () => setActionError(null),
    onSuccess: () => invalidateAll(),
    onError: (e: unknown) => setActionError(e instanceof Error ? e.message : "Could not retry this run"),
  });

  const firstFailedRunId = runs.find((r) => r.status === "failed")?.id;

  if (!workflowId) return null;
  const wf = insights.data?.workflow;
  const t = insights.data?.totals;
  const tokensTotal = (t?.tokensIn ?? 0) + (t?.tokensOut ?? 0);
  const detail = runDetail.data;

  // A run's chat lives in the agent's "Workflows" channel, and the chat only
  // keeps a session that belongs to the channel in the URL: without channelId
  // it fell back to General and opened some other conversation. Look the
  // channel up from the agent's session list (the chat's own query, so it is
  // usually cached), as the notification bell already passes it.
  const openTranscript = async () => {
    const sessionId = detail?.run.sessionId;
    const agentId = detail?.run.assignedAgent?.id;
    if (!sessionId || !agentId) return;
    const params = new URLSearchParams({ agentId, sessionId });
    const sessions = await queryClient
      .fetchQuery({
        queryKey: ["chat-sessions", agentId],
        queryFn: () => chatApi.listSessions(agentId),
      })
      .catch(() => []);
    const channelId = sessions.find((s) => s.id === sessionId)?.channelId;
    if (channelId) params.set("channelId", channelId);
    touchAgent.mutate(agentId);
    navigate(`/tenants/${tenantId}/workspace?${params}`);
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      {/* Top bar. Matched to the runs and evaluation pages — same fill, same
          padding, same 14px title — so the bar does not shift as you move
          between the three with the switcher. */}
      <div className="flex shrink-0 items-center gap-3 border-b border-border bg-surface px-4 py-2">
        {/* The workflow's own name, nothing above it. The trail read
            "Workflows / Insights / Name" when the page you came from is one
            click back, so the arrow carries that job and the name gets the
            room. Matches the runs and evaluation pages. */}
        <button
          type="button"
          onClick={() =>
            navigate(
              withWorkspaceContext(
                `/tenants/${tenantId}/workflows/${workflowId}`,
                searchParams,
              ),
            )
          }
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary"
          aria-label="Back to canvas"
        >
          <ArrowLeft size={14} />
        </button>
        <div className="flex min-w-0 flex-1 items-center gap-2 text-sm">
          <span className="truncate font-semibold text-text-primary">{wf?.name ?? "…"}</span>
          {wf && <StatusPill status={wf.status} />}
        </div>
        {/* Same switcher, same place as the runs and evaluation pages. This
            page used to be the one you could reach but not leave. */}
        {tenantId && workflowId && (
          <WorkflowTabs tenantId={tenantId} workflowId={workflowId} active="insights" />
        )}
      </div>

      {/* This page's own controls, in the row the node chain used to fill.
          The chain listed the workflow's shape, which the step panel on the
          right already shows for the run you are looking at — and it named
          every agent step after the AGENT, so one agent doing five jobs read
          as the same word five times. */}
      <div className="flex shrink-0 items-center gap-2 px-4 pt-3">
        <span className="flex-1" />
        <InsightsRangePicker value={range} onChange={setRange} />
        <button
          type="button"
          onClick={() => navigate(withWorkspaceContext(`/tenants/${tenantId}/workflows/${workflowId}`, searchParams))}
          className="inline-flex h-[26px] items-center rounded-md border border-border px-[9px] text-[12px] font-medium text-text-primary transition-colors hover:bg-surface-hover"
        >
          Open canvas
        </button>
        <button
          type="button"
          onClick={() => runNow.mutate()}
          disabled={runNow.isPending || wf?.status !== "active"}
          title={wf && wf.status !== "active" ? "Activate the workflow on the canvas to run it" : undefined}
          className="inline-flex h-[26px] items-center gap-1.5 rounded-md bg-[#10a37f] px-[10px] text-[12px] font-medium text-white transition-colors hover:bg-[#0d8a6a] disabled:opacity-50"
        >
          {runNow.isPending && <Loader2 size={12} className="animate-spin" />}
          Run now
        </button>
      </div>

      {/* KPI strip */}
      <div className="mx-6 mt-[14px] shrink-0">
        <KpiStrip
          valueSize="md"
          loading={insights.isLoading && !insights.data}
          items={[
            {
              label: `Runs · ${range.period ?? "custom"}`,
              value: formatInt(t?.runs),
              sub: t?.comparison?.runsChangePct != null ? `${formatSignedPercent(t.comparison.runsChangePct)} vs baseline` : null,
            },
            {
              label: "Success",
              value: formatPercent(t?.successRate),
              sub: t?.comparison?.successRateChangePts != null ? `${formatSignedPercent(t.comparison.successRateChangePts)} pts` : null,
              subTone: t?.comparison?.successRateChangePts != null ? (t.comparison.successRateChangePts >= 0 ? "success" : "danger") : "muted",
            },
            {
              label: "Avg duration",
              value: formatDurationMs(t?.avgDurationMs),
              sub: t?.comparison?.avgDurationChangePct != null ? `${formatSignedPercent(t.comparison.avgDurationChangePct)} vs baseline` : null,
              subTone: t?.comparison?.avgDurationChangePct != null ? (t.comparison.avgDurationChangePct <= 0 ? "success" : "danger") : "muted",
            },
            {
              label: "Tokens",
              value: tokensTotal > 0 ? formatTokens(tokensTotal) : t ? "—" : "",
              sub: t && t.cacheReadTokens + t.cacheWriteTokens > 0 ? `+ ${formatTokens(t.cacheReadTokens + t.cacheWriteTokens)} cached` : null,
              title:
                tokensTotal === 0
                  ? "No token telemetry attributed to these runs"
                  : `${formatTokens(t!.tokensIn)} in · ${formatTokens(t!.tokensOut)} out · ${formatTokens(t!.cacheReadTokens)} cache read · ${formatTokens(t!.cacheWriteTokens)} cache write`,
            },
            {
              label: "Cost",
              value: t?.priced ? formatCostCents(t.costCents) : t ? "—" : "",
              sub:
                t?.costPerRunCents != null
                  ? `${formatCostCents(t.costPerRunCents)}/run${t.comparison?.costPerRunChangePct != null ? `, ${formatSignedPercent(t.comparison.costPerRunChangePct)}` : ""}`
                  : null,
              subTone: t?.comparison?.costPerRunChangePct != null ? (t.comparison.costPerRunChangePct <= 0 ? "success" : "danger") : "muted",
            },
          ]}
        />
      </div>

      {actionError && (
        <p className="mx-6 mt-2 text-[11.5px] text-[#dc2626] dark:text-[#ef4444]">{actionError}</p>
      )}

      {/* Two-pane body */}
      <div className="flex min-h-0 flex-1">
        {/* Runs list */}
        <div className="flex min-w-0 flex-1 flex-col pl-6 pt-4">
          <div className="mb-2 flex items-center gap-2 pr-4">
            <h2 className="text-[12.5px] font-semibold text-text-primary">Runs</h2>
            <span className="flex-1" />
            <Segmented
              bordered={false}
              size="xs"
              aria-label="Run filter"
              value={runFilter}
              onChange={setRunFilter}
              options={[
                { value: "all", label: "All" },
                { value: "failed", label: `Failed${runsQuery.data ? ` · ${runsQuery.data.failedCount}` : ""}` },
              ]}
            />
          </div>
          <div className="grid gap-2.5 border-b border-border pb-1.5 pr-4 text-[11px] font-medium text-text-muted" style={{ gridTemplateColumns: RUN_GRID }}>
            <span>Run</span><span>Started</span><span className="text-right">Duration</span><span className="text-right">Tokens</span><span className="text-right">Cached</span><span className="text-right">Cost</span><span className="text-right">Status</span>
          </div>
          <div data-tour="workflow-runs" className="min-h-0 flex-1 overflow-y-auto pr-4">
            {runsQuery.isLoading && !runsQuery.data ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="grid gap-2.5 border-b border-[#f0f0ee] py-[7px] dark:border-[#1a1b1e]" style={{ gridTemplateColumns: RUN_GRID }}>
                  {Array.from({ length: 5 }).map((_, j) => <div key={j} className="h-3 animate-pulse rounded bg-surface-hover" />)}
                </div>
              ))
            ) : runs.length === 0 ? (
              <p className="py-6 text-[12px] text-text-muted">No runs in this period.</p>
            ) : (
              runs.map((r, idx) => (
                <RunRow
                  key={r.id}
                  run={r}
                  index={runs.length - idx}
                  selected={r.id === selectedRunId}
                  onSelect={() => setParam("run", r.id)}
                  // The "retry a failed run" guide points at the first one.
                  tourAnchor={r.id === firstFailedRunId ? "workflow-failed-run" : undefined}
                />
              ))
            )}
            {runsQuery.data?.hasMore && (
              <button
                type="button"
                onClick={() => setPageCount((n) => n + 1)}
                disabled={runsQuery.isFetching}
                className="my-3 w-full rounded-md border border-border py-1.5 text-[12px] text-text-secondary transition-colors hover:bg-surface-hover disabled:opacity-50"
              >
                {runsQuery.isFetching ? "Loading…" : "Load more"}
              </button>
            )}
          </div>
        </div>

        {/* Run panel */}
        <aside data-tour="workflow-run-panel" className="flex w-[372px] shrink-0 flex-col gap-[14px] border-l border-border bg-sidebar p-4 px-5 max-lg:hidden">
          {!selectedRunId ? (
            <p className="text-[12px] text-text-muted">Select a run to see its steps.</p>
          ) : !detail ? (
            <div className="flex items-center gap-2 text-[12px] text-text-muted"><Loader2 size={12} className="animate-spin" /> Loading run…</div>
          ) : (
            <RunPanel
              detail={detail}
              runNumber={runs.length ? runs.length - runs.findIndex((r) => r.id === detail.run.id) : null}
              agentById={agentById}
              onRetry={canActOnRuns && canRetryWorkflowRun(detail.run.status) ? () => retry.mutate(detail.run.id) : undefined}
              retrying={retry.isPending}
              onOpenTranscript={detail.run.sessionId && detail.run.assignedAgent ? openTranscript : undefined}
            />
          )}
        </aside>
      </div>
    </div>
  );
}

const RUN_GRID = "44px 1fr 62px 54px 58px 58px 72px";

function RunRow({ run, index, selected, onSelect, tourAnchor }: { run: InsightRunRow; index: number; selected: boolean; onSelect: () => void; tourAnchor?: string }) {
  const live = run.status === "running" || run.status === "awaiting";
  return (
    <button
      type="button"
      data-tour={tourAnchor}
      onClick={onSelect}
      aria-current={selected ? "true" : undefined}
      className={cn(
        "grid w-full items-center gap-2.5 border-b border-[#f0f0ee] py-[7px] text-left text-[12.5px] transition-colors dark:border-[#1a1b1e]",
        selected ? "-ml-2 rounded-md bg-sidebar pl-2" : "hover:bg-sidebar/60",
      )}
      style={{ gridTemplateColumns: selected ? `calc(44px + 8px) 1fr 62px 54px 58px 58px 72px` : RUN_GRID }}
    >
      <span className={cn("tabular-nums", selected ? "text-text-primary" : "text-text-muted")}>#{index}</span>
      <span className="truncate text-text-primary">{formatStartedAt(run.startedAt)}</span>
      <span className="text-right tabular-nums text-text-secondary">{live ? "…" : formatDurationMs(run.durationMs, { precise: true })}</span>
      <span
        className="text-right tabular-nums text-text-secondary"
        title={run.tokensIn + run.tokensOut > 0 ? `${formatTokens(run.tokensIn)} in · ${formatTokens(run.tokensOut)} out` : undefined}
      >
        {run.tokensIn + run.tokensOut > 0 ? formatTokens(run.tokensIn + run.tokensOut) : "—"}
      </span>
      <span
        className="text-right tabular-nums text-text-muted"
        title={
          run.cacheReadTokens + run.cacheWriteTokens > 0
            ? `${formatTokens(run.cacheReadTokens)} cache read · ${formatTokens(run.cacheWriteTokens)} cache write`
            : undefined
        }
      >
        {run.cacheReadTokens + run.cacheWriteTokens > 0 ? formatTokens(run.cacheReadTokens + run.cacheWriteTokens) : "—"}
      </span>
      <span
        className="text-right tabular-nums text-text-secondary"
        title={run.models.length ? `${run.models.join(" · ")}${run.priced ? "" : " — no catalogue price"}` : undefined}
      >
        {run.priced ? formatCostCents(run.costCents) : "—"}
      </span>
      <span className={cn("text-right text-[11.5px] font-medium", statusTextClass(run.status))}>
        {live && <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[#10a37f] align-middle [animation:status-pulse_1.4s_ease-in-out_infinite]" />}
        {statusLabel(run.status)}
      </span>
    </button>
  );
}

function statusLabel(s: string): string {
  switch (s) {
    case "completed": return "Completed";
    case "failed": return "Failed";
    case "cancelled": return "Cancelled";
    case "awaiting": return "Waiting";
    case "running": return "Running";
    default: return s;
  }
}

function statusTextClass(s: string): string {
  switch (s) {
    case "completed": return "text-[#10a37f]";
    case "failed": return "text-[#dc2626] dark:text-[#ef4444]";
    case "cancelled": return "text-text-muted";
    case "awaiting": return "text-[#d97706] dark:text-[#f59e0b]";
    case "running": return "text-[#10a37f]";
    default: return "text-text-secondary";
  }
}

function StatusPill({ status }: { status: string }) {
  const active = status === "active";
  return (
    <span
      className={cn(
        "inline-flex flex-none items-center gap-1.5 rounded-[4px] px-[7px] py-[2px] text-[11px] font-medium",
        active ? "bg-[#10a37f]/10 text-[#10a37f]" : status === "paused" ? "bg-[#d97706]/10 text-[#d97706] dark:text-[#f59e0b]" : "bg-surface-hover text-text-secondary",
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", active ? "bg-[#10a37f]" : status === "paused" ? "bg-[#d97706] dark:bg-[#f59e0b]" : "bg-text-muted")} />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function RunPanel({
  detail,
  runNumber,
  agentById,
  onRetry,
  retrying,
  onOpenTranscript,
}: {
  detail: NonNullable<ReturnType<typeof useQuery<import("@/api/workflow-insights").RunInsights>>["data"]>;
  runNumber: number | null;
  agentById: Map<string, { id: string; name: string; avatarUrl: string | null }>;
  onRetry?: () => void;
  retrying: boolean;
  onOpenTranscript?: () => void;
}) {
  const { run, steps, similarFailures } = detail;
  const live = run.status === "running" || run.status === "awaiting";
  return (
    <>
      <div className="flex items-center gap-2">
        <h3 className="text-[12.5px] font-semibold text-text-primary">Run {runNumber != null ? `#${runNumber}` : run.id.slice(0, 8)}</h3>
        {run.status === "failed" && (
          <span className="rounded-[4px] bg-[#dc2626]/[0.09] px-[7px] py-[2px] text-[11px] font-medium text-[#dc2626] dark:bg-[#ef4444]/[0.12] dark:text-[#ef4444]">
            {run.failedStepIndex ? `Failed at step ${run.failedStepIndex}` : "Failed"}
          </span>
        )}
        {run.status === "completed" && (
          <span className="rounded-[4px] bg-[#10a37f]/10 px-[7px] py-[2px] text-[11px] font-medium text-[#10a37f]">Completed</span>
        )}
        {run.status === "cancelled" && (
          <span className="rounded-[4px] bg-surface-hover px-[7px] py-[2px] text-[11px] font-medium text-text-secondary">Cancelled</span>
        )}
        {live && (
          <span className="inline-flex items-center gap-1.5 rounded-[4px] bg-[#10a37f]/10 px-[7px] py-[2px] text-[11px] font-medium text-[#10a37f]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#10a37f] [animation:status-pulse_1.4s_ease-in-out_infinite]" />
            {run.status === "awaiting" ? "Waiting" : "Running"}
          </span>
        )}
        <span className="ml-auto whitespace-nowrap text-[11px] text-text-muted">
          {formatStartedAt(run.startedAt)}{run.durationMs != null ? ` · ${formatDurationMs(run.durationMs, { precise: true })}` : ""}
        </span>
      </div>

      <ol className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {steps.map((s, i) => (
          <StepEntry key={s.stepId} step={s} isLast={i === steps.length - 1} agentById={agentById} isCurrent={live && run.currentStepId === s.stepId} />
        ))}
        {steps.length === 0 && <li className="text-[12px] text-text-muted">No steps recorded yet.</li>}
      </ol>

      <div className="mt-auto flex items-center gap-2 pt-2">
        {onRetry && (
          <button
            type="button"
            data-tour="workflow-retry"
            onClick={onRetry}
            disabled={retrying}
            className="inline-flex h-[26px] items-center gap-1.5 rounded-md bg-text-primary px-[10px] text-[12px] font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {retrying && <Loader2 size={12} className="animate-spin" />}
            Retry run
          </button>
        )}
        {onOpenTranscript && (
          <button
            type="button"
            onClick={onOpenTranscript}
            className="inline-flex h-[26px] items-center rounded-md border border-border px-[9px] text-[12px] font-medium text-text-primary transition-colors hover:bg-surface-hover"
          >
            Open transcript
          </button>
        )}
        {run.status === "failed" && (
          <span className="ml-auto text-[11px] text-text-muted">
            {similarFailures === 0 ? "No similar failures this week" : `${similarFailures} similar ${similarFailures === 1 ? "failure" : "failures"} this week`}
          </span>
        )}
      </div>
    </>
  );
}

function StepEntry({
  step,
  isLast,
  isCurrent,
  agentById,
}: {
  step: InsightStep;
  isLast: boolean;
  isCurrent: boolean;
  agentById: Map<string, { id: string; name: string; avatarUrl: string | null }>;
}) {
  const agent = step.agentId ? agentById.get(step.agentId) : undefined;
  const kind =
    step.nodeType === "trigger" ? "Trigger" : step.nodeType === "agent" ? "Agent" : step.nodeType === "condition" ? "Condition" : step.nodeType === "decision" ? "Decision" : "Action";
  const skipped = step.status === "skipped" || step.status === "pending";
  const dot =
    step.status === "completed"
      ? "bg-[#10a37f]"
      : step.status === "failed"
        ? "bg-[#dc2626] shadow-[0_0_0_3px_rgba(220,38,38,0.15)] dark:bg-[#ef4444] dark:shadow-[0_0_0_3px_rgba(239,68,68,0.2)]"
        : step.status === "running" || step.status === "awaiting"
          ? "bg-[#10a37f] [animation:status-pulse_1.4s_ease-in-out_infinite]"
          : "border-[1.5px] border-[#c4c5c9] bg-transparent dark:border-[#4d4e54]";
  const durationText =
    step.status === "skipped" ? "skipped" : step.status === "pending" ? "" : isCurrent ? "running" : formatDurationMs(step.durationMs, { precise: true });
  const subline =
    step.nodeType === "trigger"
      ? step.payloadPreview
        ? `“${step.payloadPreview}”`
        : null
      : step.nodeType === "agent" && (step.tokensIn + step.tokensOut > 0 || step.toolCalls > 0)
        ? `${formatTokens(step.tokensIn)} in · ${formatTokens(step.tokensOut)} out${
            step.cacheReadTokens + step.cacheWriteTokens > 0
              ? ` · ${formatTokens(step.cacheReadTokens + step.cacheWriteTokens)} cached`
              : ""
          } · ${step.toolCalls} tool ${step.toolCalls === 1 ? "call" : "calls"}`
        : (step.nodeType === "condition" || step.nodeType === "decision") && step.branchTaken
          ? `→ ${step.branchTaken}`
          : step.outputPreview;

  return (
    <li className="flex gap-2.5">
      <div className="flex w-[14px] flex-col items-center">
        <span className={cn("mt-[5px] h-2 w-2 flex-none rounded-full", dot)} />
        {!isLast && <span className="my-1 w-px flex-1 bg-[#dcdcd9] dark:bg-[#2a2b2f]" />}
      </div>
      <div className={cn("min-w-0 flex-1", !isLast && "pb-3")}>
        <div className="flex items-baseline gap-2">
          <span className={cn("inline-flex min-w-0 items-center gap-1.5 text-[12px] font-medium", skipped ? "text-text-muted" : "text-text-primary")}>
            <span className="truncate">
              {kind} · {step.nodeType === "agent" && agent ? null : step.label}
            </span>
            {step.nodeType === "agent" && agent && (
              <span className="inline-flex items-center gap-1.5 truncate">
                <AgentAvatar agent={agent} size={14} />
                {agent.name}
              </span>
            )}
          </span>
          <span className="ml-auto flex-none text-[11px] tabular-nums text-text-muted">{durationText}</span>
        </div>
        {subline && <p className={cn("mt-0.5 truncate text-[11.5px]", skipped ? "text-text-muted" : "text-text-secondary")} title={subline}>{subline}</p>}
        {step.error && (
          <div className="mt-1.5 rounded-md border border-[#f1c9c9] bg-background px-[9px] py-[7px] dark:border-[#ef4444]/35 dark:bg-[#17181a]">
            <p className="text-[11.5px] font-medium text-[#dc2626] dark:text-[#ef4444]">{step.error.message}</p>
            {step.error.detail && (
              <p className="mt-0.5 break-words font-mono text-[11px] text-text-secondary">{step.error.detail}</p>
            )}
          </div>
        )}
      </div>
    </li>
  );
}
